'use client';

import useUserData from './useUserData';
import { formatCompactAmount } from './formatCompactAmount';
import { formatMoney } from './money/formatMoney';
import { formatPercent as formatPercentFor } from './formatPercent';

const useLocaleCurrency = () => {
  const { currency, locale } = useUserData();

  // narrowSymbol by default because Intl treats the Chilean peso as foreign under
  // en-US and falls back to the ISO code, so the same amount reads $6.683.458 in
  // Spanish and CLP 6,683,458 in English. Under es-CL the two options produce an
  // identical string, so this is a no-op there. Pass 'symbol' only where the ISO
  // code is doing work the surrounding copy does not already do: every amount in
  // the app is in the user's stored currency, so the code is redundant wherever a
  // nearby label or control already names it.
  // With no fraction digits passed, the currency decides them (CLP 0, USD 2, CLF 4)
  // through formatMoney, so this agrees with every other amount on the screen.
  const formatCurrency = (
    value: number,
    minimumFractionDigits?: number,
    maximumFractionDigits?: number,
    currencyDisplay: 'narrowSymbol' | 'symbol' = 'narrowSymbol',
  ) => {
    if (minimumFractionDigits === undefined && maximumFractionDigits === undefined) {
      return formatMoney(value, currency, locale);
    }
    return value.toLocaleString(locale, {
      style: 'currency',
      currency: currency,
      currencyDisplay,
      minimumFractionDigits,
      maximumFractionDigits,
    });
  };

  // A chart axis has no room for a full CLP magnitude, which runs to twelve
  // digits. Same locale and currency as formatCurrency, but compact notation and
  // at most one fraction digit.
  const formatCompactCurrency = (value: number) => {
    return formatCompactAmount(value, locale, currency);
  };

  const formatPercent = (value: number, fractionDigits: number = 1) =>
    formatPercentFor(value, locale, fractionDigits);

  return { formatCurrency, formatCompactCurrency, formatPercent, locale, currency };
};

export default useLocaleCurrency;
